// filepath: apps/web/src/features/lists/kanban/KanbanCountBanner.tsx
//
// FR-UI-VIEW-1 AC-3 — "X of Y records shown." count banner.
//
// Triggered when the server returns a `total` field that exceeds
// `records.length` AND `records.length` has reached the server-enforced
// cap (KANBAN_RECORDS_CAP = 500). The banner's copy is locked — the
// user must not see localisation drift on the cap message; the wire
// naming is API-stable (ARCHITECTURE_WEB.md §5 detail pages).
//
// INVARIANT REMINDERS:
//   - The banner is off by default — it surfaces only when the
//     server indicates a partial result.
//   - The chrome line is a single static sentence; no interpretation
//     of the value (the SPA never formats "12 of 5,123 records shown"
//     with arbitrary locale-aware number formatting on this surface —
//     the count is operational, not visualised).
//   - The banner does NOT use `Banner`/`Card` — it is a plain
//     informational notice with `role="status"` so screen readers
//     receive a polite announcement when it appears, without
//     stealing focus.

import React from "react";

export interface KanbanCountBannerProps {
  /** Number of records currently rendered. */
  shown: number;
  /**
   * Server-supplied total record count (across all columns). Null
   * when the server wire omits the additive `total` field (older
   * deployments) — the banner stays off in that case.
   */
  total: number | null;
  /**
   * Cap above which the banner is considered active. Defaults to
   * `KANBAN_RECORDS_CAP` (500). Exposed as a prop so a future FR
   * bumping the cap can pass a different threshold.
   */
  cap?: number;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * Predicate — should the banner be visible? Per FR-UI-VIEW-1 AC-3,
 * the banner triggers when the server caps the result set, i.e.
 * `shown >= cap` AND `total > cap`.
 *
 * `total < shown` is impossible under the wire contract (server
 * always supplies total ≥ page size) but we treat it as `false` to
 * remain defensive against a misbehaving backend (banner stays off
 * when the data is internally inconsistent).
 */
export function shouldShowCountBanner(
  shown: number,
  total: number | null,
  cap: number,
): boolean {
  if (total === null) return false;
  if (!Number.isFinite(total) || total <= 0) return false;
  if (!Number.isFinite(shown) || shown < 0) return false;
  if (total <= cap) return false;
  if (shown < cap) return false;
  return shown < total;
}

/**
 * KanbanCountBanner — locked-copy partial-result notice.
 *
 * Renders only when `shouldShowCountBanner(shown, total, cap)`
 * returns true. Otherwise returns `null` (the parent does not
 * need to track visibility).
 */
export function KanbanCountBanner({
  shown,
  total,
  cap = 500,
  "data-testid": testId = "kanban-count-banner",
}: KanbanCountBannerProps): React.ReactElement | null {
  if (!shouldShowCountBanner(shown, total, cap)) {
    return null;
  }
  // LOCKED COPY — see file header.
  const message = `${shown} of ${total} records shown.`;
  return (
    <p
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-kanban-shown={shown}
      data-kanban-total={total}
      data-kanban-cap={cap}
      className="kanban-count-banner"
      style={{
        margin: "0 0 12px 0",
        padding: "8px 16px",
        background: "var(--surface-sunken)",
        color: "var(--text-body)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-sm)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-sm)",
      }}
    >
      {message}
    </p>
  );
}
